// Given a polygon and lists of horizontal and vertical cutting planes,
// return a list of sliced polygons

function clipPolygon(polygon, inside, intersect) {
  if (polygon.length === 0) return [];
  const output = [];
  let prev = polygon[polygon.length - 1];
  let prevIn = inside(prev);
  for (const curr of polygon) {
    const currIn = inside(curr);
    if (currIn) {
      if (!prevIn) output.push(intersect(prev, curr));
      output.push(curr);
    } else if (prevIn) {
      output.push(intersect(prev, curr));
    }
    prev = curr;
    prevIn = currIn;
  }
  return output;
}

function clipToRect(polygon, xmin, ymin, xmax, ymax) {
  // Left edge: keep x >= xmin
  polygon = clipPolygon(
    polygon,
    (p) => p.x >= xmin,
    (a, b) => {
      const t = (xmin - a.x) / (b.x - a.x);
      return { x: xmin, y: a.y + t * (b.y - a.y) };
    },
  );

  // Right edge: keep x <= xmax
  polygon = clipPolygon(
    polygon,
    (p) => p.x <= xmax,
    (a, b) => {
      const t = (xmax - a.x) / (b.x - a.x);
      return { x: xmax, y: a.y + t * (b.y - a.y) };
    },
  );

  // Bottom edge: keep y >= ymin
  polygon = clipPolygon(
    polygon,
    (p) => p.y >= ymin,
    (a, b) => {
      const t = (ymin - a.y) / (b.y - a.y);
      return { x: a.x + t * (b.x - a.x), y: ymin };
    },
  );

  // Top edge: keep y <= ymax
  polygon = clipPolygon(
    polygon,
    (p) => p.y <= ymax,
    (a, b) => {
      const t = (ymax - a.y) / (b.y - a.y);
      return { x: a.x + t * (b.x - a.x), y: ymax };
    },
  );

  return polygon;
}

function polygonArea(polygon) {
  const n = polygon.length;
  if (n < 3) return 0;
  let s = 0;
  for (let i = 0; i < n; i++) {
    const { x: x1, y: y1 } = polygon[i];
    const { x: x2, y: y2 } = polygon[(i + 1) % n];
    s += x1 * y2 - x2 * y1;
  }
  return s * 0.5;
}

const eps = 1e-8;
/**
 * Slice a polygon by horizontal (y = const) and vertical (x = const)
 * cutting planes and return the resulting pieces.
 *
 * The polygon may be given in either winding order. Cuts that fall
 * outside the polygon's bounding box are ignored, and duplicate cut
 * values are deduplicated. If no cuts are provided, the original
 * polygon is returned as a single piece.
 *
 */
function slicePolygon(polygon, yCuts, xCuts) {
  if (!polygon || polygon.length < 3) return [];

  let polyXmin = Infinity;
  let polyXmax = -Infinity;
  let polyYmin = Infinity;
  let polyYmax = -Infinity;
  for (const { x, y } of polygon) {
    if (x < polyXmin) polyXmin = x;
    if (x > polyXmax) polyXmax = x;
    if (y < polyYmin) polyYmin = y;
    if (y > polyYmax) polyYmax = y;
  }

  // Build strictly sorted, deduplicated cell boundaries. Pad with the
  // polygon's own bounding box so we cover the whole shape even when the
  // caller passes cuts that don't span it (or passes none at all).
  // Drop cuts that fall completely outside the polygon's bounding box.
  const xBounds = [
    ...new Set([
      polyXmin,
      polyXmax,
      ...xCuts.filter((x) => x >= polyXmin && x <= polyXmax),
    ]),
  ].sort((a, b) => a - b);
  const yBounds = [
    ...new Set([
      polyYmin,
      polyYmax,
      ...yCuts.filter((y) => y >= polyYmin && y <= polyYmax),
    ]),
  ].sort((a, b) => a - b);

  const pieces = [];
  for (let i = 0; i < xBounds.length - 1; i++) {
    const xmin = xBounds[i];
    const xmax = xBounds[i + 1];
    if (xmax - xmin <= 0) continue;
    for (let j = 0; j < yBounds.length - 1; j++) {
      const ymin = yBounds[j];
      const ymax = yBounds[j + 1];
      if (ymax - ymin <= 0) continue;
      const piece = clipToRect(polygon, xmin, ymin, xmax, ymax);
      if (piece.length >= 3 && Math.abs(polygonArea(piece)) > eps) {
        pieces.push(piece);
      }
    }
  }
  return pieces;
}

export { slicePolygon, polygonArea };
